import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';

// Async thunk to fetch reviews by package
export const fetchReviewsByPackage = createAsyncThunk(
    'reviews/fetchByPackage',
    async ({ packageId, tourType, page = 1, limit = 6 }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/review/get-review-bypackage/${packageId}`, {
                params: { tourType, page, limit },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch reviews');
        }
    }
);

// Async thunk to fetch all reviews with status filtering
export const fetchAllReviews = createAsyncThunk(
    'reviews/fetchAll',
    async ({ status = '', page = 1, limit = 10 }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get('/review/get-all-reviews', {
                params: { status, page, limit },
                withCredentials: true,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch all reviews');
        }
    }
);

// Async thunk to fetch reviews by tour type
export const fetchReviewsByTourType = createAsyncThunk(
    'reviews/fetchByTourType',
    async ({ tourType, page = 1, limit = 6 }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/review/get-review-bytourType/${tourType}`, {
                params: { page, limit },
            });
            console.log("bytourtype", response)
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch reviews by tour type');
        }
    }
);

// Async thunk to fetch review media by tour type
export const fetchReviewMediaByTourType = createAsyncThunk(
    'reviews/fetchMediaByTourType',
    async (tourType, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/review/get-review-image-bytourType/${tourType}/media`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch review media');
        }
    }
);

// Async thunk to create a review
export const createReview = createAsyncThunk(
    'reviews/create',
    async ({ packageId, tourType, rating, comment, mediaFiles, mediaData }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            formData.append('packageId', packageId);
            formData.append('tourType', tourType);
            formData.append('rating', rating);
            formData.append('comment', comment);
            if (mediaData) {
                formData.append('media', JSON.stringify(mediaData));
            }
            if (mediaFiles) {
                mediaFiles.forEach(file => formData.append('reviewMedia', file));
            }

            const response = await axiosInstance.post('/review/create-review', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create review');
        }
    }
);

// Async thunk to update a review
export const updateReview = createAsyncThunk(
    'reviews/update',
    async ({ id, rating, comment, mediaFiles, mediaData }, { rejectWithValue }) => {
        try {
            const formData = new FormData();
            if (rating) formData.append('rating', rating);
            if (comment) formData.append('comment', comment);
            if (mediaData) {
                formData.append('media', JSON.stringify(mediaData));
            }
            if (mediaFiles) {
                mediaFiles.forEach(file => formData.append('reviewMedia', file));
            }

            const response = await axiosInstance.put(`/review/update-review/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update review');
        }
    }
);

// Async thunk to delete a review
export const deleteReview = createAsyncThunk(
    'reviews/delete',
    async (id, { rejectWithValue }) => {
        try {
            await axiosInstance.delete(`/review/delete-review/${id}`, {
                withCredentials: true,
            });
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete review');
        }
    }
);

// Async thunk to delete a review as admin
export const adminDeleteReview = createAsyncThunk(
    'reviews/adminDelete',
    async (id, { rejectWithValue }) => {
        try {
            await axiosInstance.delete(`/review/admin/delete-review/${id}`, {
                withCredentials: true,
            });
            return id; // return deleted review id
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete review as admin');
        }
    }
);

// Async thunk to moderate a review
export const moderateReview = createAsyncThunk(
    'reviews/moderate',
    async ({ id, status }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`/review/moderate/${id}`, { status }, {
                withCredentials: true,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to moderate review');
        }
    }
);

const reviewSlice = createSlice({
    name: 'reviews',
    initialState: {
        reviews: [],
        total: 0,
        pages: 1,
        currentPage: 1,
        media: [],
        mediaTotal: 0,
        loading: false,
        error: null,
        success: false,
    },
    reducers: {
        resetReviewState: (state) => {
            state.reviews = [];
            state.total = 0;
            state.pages = 1;
            state.currentPage = 1;
            state.media = [];
            state.mediaTotal = 0;
            state.error = null;
            state.success = false;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearSuccess: (state) => {
            state.success = false;
        },
    },
    extraReducers: (builder) => {
        // Fetch all reviews
        builder
            .addCase(fetchAllReviews.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllReviews.fulfilled, (state, action) => {
                state.loading = false;
                state.reviews = action.payload.reviews;
                state.total = action.payload.total;
                state.pages = action.payload.pages;
                state.currentPage = action.payload.currentPage;
            })
            .addCase(fetchAllReviews.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Fetch reviews by package
        builder
            .addCase(fetchReviewsByPackage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchReviewsByPackage.fulfilled, (state, action) => {
                state.loading = false;
                state.reviews = action.payload.reviews;
                state.total = action.payload.total;
                state.pages = action.payload.pages;
                state.currentPage = action.payload.currentPage;
            })
            .addCase(fetchReviewsByPackage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Fetch reviews by tour type
        builder
            .addCase(fetchReviewsByTourType.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchReviewsByTourType.fulfilled, (state, action) => {
                state.loading = false;
                state.reviews = action.payload.reviews;
                state.total = action.payload.total;
                state.pages = action.payload.pages;
                state.currentPage = action.payload.currentPage;
            })
            .addCase(fetchReviewsByTourType.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Fetch review media by tour type
        builder
            .addCase(fetchReviewMediaByTourType.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchReviewMediaByTourType.fulfilled, (state, action) => {
                state.loading = false;
                state.media = action.payload.media;
                state.mediaTotal = action.payload.total;
            })
            .addCase(fetchReviewMediaByTourType.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Create review
        builder
            .addCase(createReview.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createReview.fulfilled, (state, action) => {
                state.loading = false;
                state.reviews.unshift(action.payload);
                state.total += 1;
                state.success = true;
            })
            .addCase(createReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Update review
        builder
            .addCase(updateReview.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateReview.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.reviews.findIndex(review => review._id === action.payload._id);
                if (index !== -1) {
                    state.reviews[index] = action.payload;
                }
                state.success = true;
            })
            .addCase(updateReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Delete review
        builder
            .addCase(deleteReview.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deleteReview.fulfilled, (state, action) => {
                state.loading = false;
                state.reviews = state.reviews.filter(review => review._id !== action.payload);
                state.total -= 1;
                state.success = true;
            })
            .addCase(deleteReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(adminDeleteReview.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(adminDeleteReview.fulfilled, (state, action) => {
                state.loading = false;
                state.reviews = state.reviews.filter(review => review._id !== action.payload);
                state.total -= 1;
                state.success = true;
            })
            .addCase(adminDeleteReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Moderate review
        builder
            .addCase(moderateReview.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(moderateReview.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.reviews.findIndex(review => review._id === action.payload._id);
                if (index !== -1) {
                    state.reviews[index] = action.payload;
                }
                state.success = true;
            })
            .addCase(moderateReview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { resetReviewState, clearError, clearSuccess } = reviewSlice.actions;
export default reviewSlice.reducer;